/* ============================================================
   Dathent — Create Post screen
   Flow: compose → generating → done.
   Source: design pack pages/create-post.jsx + Create Post.html.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import { useActivity } from '../components/AppShell';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { useContentFeedContext } from '../state/ContentFeedContext.jsx';
import { ActiveJobsWidget } from '../components/dashboard';

const PLATFORM_OPTIONS = [
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook', name: 'Facebook' },
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'x', name: 'X' },
  { id: 'tiktok', name: 'TikTok' },
  { id: 'threads', name: 'Threads' },
  { id: 'telegram', name: 'Telegram' },
];

const RECENT_TOPIC_FALLBACKS = [
  '30-day plan for launch',
  'Behind-the-scenes process series',
  'Customer story carousel',
  'Educational series',
];

// Read a File into a data URL (base64) via FileReader.
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error || new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

function errMessage(err) {
  if (!err) return 'Unknown error';
  return err?.body?.error?.message || err?.body?.message || err?.message || 'Unknown error';
}

/* ============================================================
   Compose form
   ============================================================ */
function ComposeForm({ onGenerated }) {
  const navigate = useNavigate();
  const { socialAccounts, entitlements, ensureDashboardData } = useDashboardData() || {};
  const { startGenerationBatch } = useContentFeedContext();
  const { setActivity } = useActivity();

  const [prompt, setPrompt] = useState('');
  // Selected publish target accounts (social_account.id). Multiple accounts of the
  // same platform are independent — e.g. user can pick both Telegram channels.
  const [selectedAccounts, setSelectedAccounts] = useState(() => new Set());
  const [autoPickedDefault, setAutoPickedDefault] = useState(false);
  const [count, setCount] = useState(1);
  const [schedule, setSchedule] = useState('now'); // 'now' | 'spread' | 'draft'
  const [submitting, setSubmitting] = useState(false);
  const [brand, setBrand] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [recentTopics, setRecentTopics] = useState([]);
  const [attachments, setAttachments] = useState([]); // [{ id, name, url, mimeType, uploading, error }]
  const fileInputRef = useRef(null);

  // Refresh dashboard data + load brand profile, strategy, recent batches
  useEffect(() => {
    ensureDashboardData?.();
    api.profile.getBrandProfile()
      .then((res) => setBrand(res?.data?.brandProfile || res?.brandProfile || res?.data || res))
      .catch(() => {});
    // Strategy (pillars, audience etc) — may 404 when no content plan yet.
    // Pillars.exampleTopics drives the "Suggested topics" chips below the prompt.
    api.strategy.get()
      .then((res) => setStrategy(res?.data?.strategy || res?.strategy || res?.data || res))
      .catch(() => setStrategy(null));
    // Recent batch topics from user's history (fallback when no pillars yet)
    api.content.listBatches({ limit: 12 })
      .then((res) => {
        const batches = res?.batches || res?.data?.batches || res?.data || [];
        const seen = new Set();
        const uniqueTopics = [];
        for (const b of batches) {
          const topic = (b?.topic || b?.title || '').trim();
          if (!topic || topic.length < 4) continue;
          const key = topic.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          uniqueTopics.push(topic);
          if (uniqueTopics.length >= 6) break;
        }
        setRecentTopics(uniqueTopics);
      })
      .catch(() => setRecentTopics([]));
  }, [ensureDashboardData]);

  // Topic chip suggestions — pull from strategy pillars first (the curated content
  // plan topics) and fall back to the user's batch history when no pillars yet.
  const suggestedTopics = useMemo(() => {
    const pillars = strategy?.contentPillars || strategy?.pillars || [];
    const fromPillars = [];
    for (const p of pillars) {
      const examples = p?.exampleTopics || p?.exampleIdeas || [];
      for (const ex of examples) {
        const t = String(ex).trim();
        if (!t) continue;
        if (!fromPillars.includes(t)) fromPillars.push(t);
        if (fromPillars.length >= 8) break;
      }
      if (fromPillars.length >= 8) break;
    }
    if (fromPillars.length > 0) return fromPillars;
    return recentTopics;
  }, [strategy, recentTopics]);

  // All connected accounts, grouped by platform (preserves multiple per platform).
  const accountsByPlatform = useMemo(() => {
    const groups = new Map();
    for (const acc of socialAccounts || []) {
      const p = String(acc.platform || '').toLowerCase();
      if (!p) continue;
      if (!groups.has(p)) groups.set(p, []);
      groups.get(p).push(acc);
    }
    return groups;
  }, [socialAccounts]);

  // Auto-pick first connected account on initial socialAccounts load (one-shot).
  useEffect(() => {
    if (autoPickedDefault) return;
    if (!socialAccounts?.length) return;
    if (selectedAccounts.size === 0) {
      setSelectedAccounts(new Set([socialAccounts[0].id]));
    }
    setAutoPickedDefault(true);
  }, [socialAccounts, autoPickedDefault, selectedAccounts]);

  const toggleAccount = (accountId) => {
    setSelectedAccounts((curr) => {
      const next = new Set(curr);
      next.has(accountId) ? next.delete(accountId) : next.add(accountId);
      return next;
    });
  };

  const selectedAccountIds = useMemo(() => Array.from(selectedAccounts), [selectedAccounts]);

  // Derived: unique platforms from the selected accounts — passed to batch payload.
  const selectedPlatforms = useMemo(() => {
    const set = new Set();
    for (const acc of socialAccounts || []) {
      if (selectedAccounts.has(acc.id)) {
        const p = String(acc.platform || '').toLowerCase();
        if (p) set.add(p);
      }
    }
    return Array.from(set);
  }, [socialAccounts, selectedAccounts]);

  const remainingCredits = (() => {
    const u = entitlements?.usage || {};
    const q = u.ai_generations_monthly || u.publish_ready_posts_monthly;
    if (!q) return null;
    if (q.remaining === null) return 'Unlimited';
    return q.remaining;
  })();

  // Brand defaults aggregated from brand_profile + content_strategy.
  // Strategy wins when both have a value (it's derived from the brand pass).
  const brandDefaults = useMemo(() => {
    const s = strategy || {};
    const b = brand || {};
    // Canonical language source (in priority order):
    //   1. brand.sourceMetadata.languages — full array from Edit Strategy
    //      (preserves both primary + any extras the user picked).
    //   2. brand.language — the primary single-language column. The Edit
    //      Strategy form always writes this on save, so it's the most
    //      reliable single source.
    //   3. strategy.language — derived from brand at strategy-generation
    //      time. Lagging indicator if brand was edited after.
    //
    // Build a unique ordered array so the UI can show "UK · EN" when there
    // are multiple, or just "UK" when single.
    const metaLanguages = Array.isArray(b?.sourceMetadata?.languages)
      ? b.sourceMetadata.languages.filter(Boolean)
      : [];
    const primaryFromBrand = b?.language ? [b.language] : [];
    const primaryFromStrategy = s?.language ? [s.language] : [];
    const combined = [...metaLanguages, ...primaryFromBrand, ...primaryFromStrategy];
    const languages = Array.from(new Set(combined.map((l) => String(l).toLowerCase()).filter(Boolean)));
    return {
      audience: s.audience || s.targetAudience || b.audience || b.targetAudience,
      tone: s.tone || b.tone || b.brandVoice,
      visual: s.visualStyle || s.visual || b.visualStyle || b.visual,
      languages,
      goal: s.goal || b.goal,
    };
  }, [strategy, brand]);

  // Final chip list — pillar topics preferred, then batch history, then canned fallback.
  const topics = suggestedTopics.length > 0 ? suggestedTopics : RECENT_TOPIC_FALLBACKS;
  const topicsLabel = (() => {
    if (strategy?.contentPillars?.some((p) => (p.exampleTopics || []).length > 0)) return 'From your content plan';
    if (recentTopics.length > 0) return 'Recent topics';
    return 'Suggested topics';
  })();

  // Shared upload pipeline used by both the file picker and drag-and-drop.
  const uploadFiles = async (files) => {
    for (const file of files) {
      const tempId = `tmp-${Date.now()}-${file.name}`;
      setAttachments((curr) => [...curr, { id: tempId, name: file.name, mimeType: file.type, uploading: true }]);
      try {
        const dataUrl = await readFileAsDataUrl(file);
        const res = await api.assistant.uploadAttachment({
          dataUrl,
          originalFilename: file.name,
          mimeType: file.type,
          type: file.type.startsWith('image/') ? 'image' : 'file',
        });
        const asset = res?.asset || res?.data?.asset || res;
        setAttachments((curr) => curr.map((a) => a.id === tempId
          ? { id: asset?.id || tempId, name: file.name, mimeType: file.type, url: asset?.publicUrl || asset?.url, uploading: false }
          : a));
      } catch (err) {
        setAttachments((curr) => curr.map((a) => a.id === tempId
          ? { ...a, uploading: false, error: errMessage(err) }
          : a));
      }
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // reset so user can re-pick same file
    await uploadFiles(files);
  };

  // Drag-and-drop state + handlers
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.items?.length > 0) setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    await uploadFiles(files);
  };

  const removeAttachment = (id) => {
    setAttachments((curr) => curr.filter((a) => a.id !== id));
  };

  const handleGenerate = async () => {
    if (prompt.trim().length < 3) {
      window.alert('Describe the batch in a few words (minimum 3 characters).');
      return;
    }
    if (selectedAccounts.size === 0) {
      window.alert('Pick at least one account to publish to.');
      return;
    }

    // For non-draft mode we need a publish target account
    if (schedule !== 'draft' && selectedAccountIds.length === 0) {
      window.alert('Pick connected accounts or save as drafts.');
      return;
    }

    const scheduleMode = schedule === 'now' ? 'publish_now'
      : schedule === 'spread' ? 'spread'
      : 'draft';

    setSubmitting(true);
    setActivity({ kind: 'planning', step: 'Starting batch…', project: prompt.slice(0, 80) });
    try {
      const referenceAssetIds = attachments
        .filter((a) => a.id && !String(a.id).startsWith('tmp-') && !a.error)
        .map((a) => a.id);
      const referenceUrls = attachments
        .filter((a) => a.url && !a.error)
        .map((a) => a.url);
      // Derive format from the selected platforms. Almost every modern
      // network gets MUCH better reach with an image — Telegram channels,
      // Threads, Facebook all live or die on visuals as much as Instagram
      // does. Default to image_post whenever the batch touches any of
      // those, only fall back to text_post for the genuinely text-first
      // networks (LinkedIn, X). The text-only fallback path inside
      // content-worker still catches downstream visual failures, so the
      // image_post format is safe to default to — and matches user
      // expectations: they ask for a post about their product, they
      // expect a picture, not raw copy.
      const IMAGE_FORMATS = ['instagram', 'tiktok', 'telegram', 'threads', 'facebook'];
      const formatForBatch = selectedPlatforms.some((p) => IMAGE_FORMATS.includes(p))
        ? 'image_post'
        : 'text_post';
      const payload = {
        topic: prompt.trim(),
        platforms: selectedPlatforms,
        targetAccountIds: selectedAccountIds,
        format: formatForBatch,
        formats: [formatForBatch],
        count,
        scheduleMode,
        scheduleTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        scheduleStartAt: schedule === 'spread' ? new Date().toISOString() : null,
        confirmCreditSpend: true,
        useContentPlan: true,
        referenceAssetIds: referenceAssetIds.length > 0 ? referenceAssetIds : undefined,
        referenceUrls: referenceUrls.length > 0 ? referenceUrls : undefined,
      };
      const res = await api.content.createBatch(payload);
      // API client unwraps { ok, data } → res === { batch, jobs }
      const batch = res?.batch || res?.data?.batch || res;
      // Hand off to ContentFeed so the polling loop picks it up
      if (startGenerationBatch && batch?.id) {
        startGenerationBatch(batch);
      }
      onGenerated?.(batch);
    } catch (err) {
      setActivity(null);
      window.alert(`Generate failed: ${errMessage(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cp-compose">
      <div className="cp-main">
        {/* Prompt */}
        <section className="card cp-card">
          <header className="cp-card-h">
            <div className="eyebrow"><span>What should we generate?</span></div>
            <h3>Describe the batch in plain words.</h3>
          </header>
          <div
            className={`cp-prompt ${dragging ? 'is-dragging' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <textarea
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. 30 posts for our winter launch. Drag an image here to use it as a visual reference."
            />
            {dragging && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  border: '2px dashed var(--accent)',
                  borderRadius: 'var(--r-md)',
                  background: 'var(--accent-soft)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent-text)',
                  fontSize: 14,
                  fontWeight: 500,
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
              >
                Drop to attach as reference
              </div>
            )}
            <div className="cp-prompt-foot">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,application/pdf,.doc,.docx,.txt"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <button
                className="cp-tool"
                title="Attach a reference image or file"
                type="button"
                onClick={() => fileInputRef.current?.click()}
              >
                <I.paperclip size={14} />
              </button>
              <button className="cp-tool" title="Templates (soon)" type="button" disabled>
                <I.layers size={14} />
              </button>
            </div>
          </div>

          {attachments.length > 0 && (
            <div className="cp-attachments" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: -8 }}>
              {attachments.map((a) => (
                <span
                  key={a.id}
                  className="cp-sugg-pill"
                  style={{
                    background: a.error ? 'var(--danger-soft)'
                      : a.uploading ? 'var(--bg-subtle)'
                      : 'var(--success-soft)',
                    color: a.error ? 'var(--danger-text)'
                      : a.uploading ? 'var(--text-muted)'
                      : 'var(--success-text)',
                  }}
                  title={a.error || (a.uploading ? 'Uploading…' : 'Attached')}
                >
                  <I.paperclip size={11} />
                  <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.name}
                  </span>
                  {a.uploading && <span style={{ marginLeft: 4 }}>…</span>}
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    style={{ marginLeft: 4, background: 'transparent', border: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                    aria-label="Remove attachment"
                  >
                    <I.x size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="cp-sugg">
            <span className="cp-sugg-l">{topicsLabel}</span>
            {topics.map((t, i) => (
              <button key={i} className="cp-sugg-pill" onClick={() => setPrompt(t)} type="button">
                {t}
              </button>
            ))}
          </div>
        </section>

        {/* Platforms */}
        <section className="card cp-card">
          <header className="cp-card-h">
            <div className="eyebrow"><span>Publish to</span></div>
            <h3>Which channels?</h3>
          </header>
          <div className="cp-plats">
            {/* Render every connected account as its own row so multi-account
                workspaces (e.g. two Telegrams) can target each one independently. */}
            {PLATFORM_OPTIONS.flatMap((p) => {
              const accs = accountsByPlatform.get(p.id) || [];
              if (accs.length === 0) {
                return [(
                  <button
                    key={`${p.id}-none`}
                    className="cp-plat is-disabled"
                    type="button"
                    onClick={() => {}}
                    title="Not connected — go to Settings to connect"
                  >
                    <span
                      className={`ob-net-ic ob-net-ic-${p.id}`}
                      style={{ width: 28, height: 28 }}
                    >
                      {BrandIcons[p.id]?.({ size: 14 })}
                    </span>
                    <div className="cp-plat-text">
                      <div className="cp-plat-name">{p.name}</div>
                      <div className="cp-plat-sub">Not connected</div>
                    </div>
                    <span className="cp-plat-toggle" />
                  </button>
                )];
              }
              return accs.map((acc) => {
                const on = selectedAccounts.has(acc.id);
                return (
                  <button
                    key={acc.id}
                    className={`cp-plat ${on ? 'is-on' : ''}`}
                    onClick={() => toggleAccount(acc.id)}
                    type="button"
                  >
                    <span
                      className={`ob-net-ic ob-net-ic-${p.id}`}
                      style={{ width: 28, height: 28 }}
                    >
                      {BrandIcons[p.id]?.({ size: 14 })}
                    </span>
                    <div className="cp-plat-text">
                      <div className="cp-plat-name">{p.name}</div>
                      <div className="cp-plat-sub" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{acc.displayName || acc.externalAccountId || 'Connected'}</span>
                        {/* Language chip — sourced from per-account
                            publishLanguage set in Settings → Connected
                            accounts. Falls back to the org default so users
                            see which language THIS account will ship in
                            (without diving into settings). */}
                        {(acc.publishLanguage || acc.defaultPublishLanguage) && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '1px 6px',
                            fontSize: 10, fontFamily: 'var(--font-mono)',
                            letterSpacing: '0.04em', textTransform: 'uppercase',
                            background: 'var(--accent-soft)',
                            color: 'var(--accent)',
                            borderRadius: 4,
                            border: '1px solid var(--border-2)',
                          }}
                          title={acc.publishLanguage ? 'Per-account language' : 'Default language'}
                          >
                            🌐 {String(acc.publishLanguage || acc.defaultPublishLanguage).toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`cp-plat-toggle ${on ? 'is-on' : ''}`}>
                      {on && <I.check size={11} />}
                    </span>
                  </button>
                );
              });
            })}
          </div>
          {(!socialAccounts || socialAccounts.length === 0) && (
            <p style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginTop: 10 }}>
              No accounts connected yet. <Link to="/app/settings?tab=accounts">Connect one in Settings</Link> or save as drafts below.
            </p>
          )}
        </section>

        {/* Volume + schedule */}
        <section className="card cp-card">
          <header className="cp-card-h">
            <div className="eyebrow"><span>Volume & schedule</span></div>
            <h3>How many, when?</h3>
          </header>
          <div className="cp-grid-2">
            <div className="cp-field">
              <label className="cp-label">Number of posts</label>
              <div className="cp-stepper">
                <button onClick={() => setCount((c) => Math.max(1, c - 5))} type="button">−</button>
                <div className="cp-stepper-v">
                  <span className="cp-stepper-num">{count}</span>
                  <span className="cp-stepper-unit">post{count === 1 ? '' : 's'}</span>
                </div>
                <button onClick={() => setCount((c) => Math.min(100, c + 5))} type="button">+</button>
              </div>
              <div className="cp-stepper-presets">
                {[7, 14, 30, 60].map((n) => (
                  <button key={n} className={count === n ? 'is-active' : ''} onClick={() => setCount(n)} type="button">
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="cp-field">
              <label className="cp-label">Schedule</label>
              <div className="cp-radio-group">
                <button className={`cp-radio ${schedule === 'now' ? 'is-active' : ''}`} onClick={() => setSchedule('now')} type="button">
                  <span className="cp-radio-dot" />
                  <div>
                    <div className="cp-radio-l">Publish first immediately</div>
                    <div className="cp-radio-s">Rest spreads automatically.</div>
                  </div>
                </button>
                <button className={`cp-radio ${schedule === 'spread' ? 'is-active' : ''}`} onClick={() => setSchedule('spread')} type="button">
                  <span className="cp-radio-dot" />
                  <div>
                    <div className="cp-radio-l">Spread starting today</div>
                    <div className="cp-radio-s">Smart slot distribution using best-time analysis.</div>
                  </div>
                </button>
                <button className={`cp-radio ${schedule === 'draft' ? 'is-active' : ''}`} onClick={() => setSchedule('draft')} type="button">
                  <span className="cp-radio-dot" />
                  <div>
                    <div className="cp-radio-l">Save as drafts</div>
                    <div className="cp-radio-s">Don't schedule — pick slots later from Library.</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Right column — brand summary + cost + CTA */}
      <aside className="cp-side">
        <section className="card cp-card cp-brand-card">
          <header className="cp-card-h">
            <div className="eyebrow"><span>Brand defaults</span></div>
            <h3>Inferred from onboarding{strategy ? ' + strategy.' : '.'}</h3>
          </header>
          <ul className="cp-brand-list">
            <li>
              <span className="cp-brand-l">Audience</span>
              <span className="cp-brand-v">{brandDefaults.audience || '—'}</span>
            </li>
            <li>
              <span className="cp-brand-l">Tone</span>
              <span className="cp-brand-v">
                {Array.isArray(brandDefaults.tone) ? brandDefaults.tone.join(' · ') : (brandDefaults.tone || '—')}
              </span>
            </li>
            <li>
              <span className="cp-brand-l">Visual</span>
              <span className="cp-brand-v">{brandDefaults.visual || '—'}</span>
            </li>
            {brandDefaults.goal && (
              <li>
                <span className="cp-brand-l">Goal</span>
                <span className="cp-brand-v">{brandDefaults.goal}</span>
              </li>
            )}
            <li>
              <span className="cp-brand-l">Languages</span>
              <span className="cp-brand-v">
                {Array.isArray(brandDefaults.languages) && brandDefaults.languages.length > 0
                  ? brandDefaults.languages.join(' · ').toUpperCase()
                  : '—'}
              </span>
            </li>
          </ul>
          {/* "Edit brand defaults" goes straight to the strategy editor —
              that's where languages/tone/audience/goal/visual all live and
              get changed. The Plan overview page just READS them. */}
          <Link className="cp-brand-edit" to="/app/plan/edit-strategy">
            <I.edit size={12} /> Edit brand defaults
          </Link>
        </section>

        <section className="card cp-card cp-cost">
          <div className="cp-cost-head">
            <span className="eyebrow"><span>Cost</span></span>
            {entitlements?.planName && (
              <span className="pill pill-success" style={{ height: 22 }}>
                <I.check size={10} /> {entitlements.planName}
              </span>
            )}
          </div>
          <div className="cp-cost-row">
            <span>
              {count} post{count === 1 ? '' : 's'} × {selectedAccounts.size} account{selectedAccounts.size === 1 ? '' : 's'}
            </span>
            <span className="mono">{count * Math.max(1, selectedAccounts.size)} credits</span>
          </div>
          {remainingCredits != null && (
            <div className="cp-cost-row cp-cost-sub">
              <span>Remaining this month</span>
              <span className="mono">{remainingCredits}</span>
            </div>
          )}
          <button
            className="btn btn-primary cp-generate"
            onClick={handleGenerate}
            disabled={submitting}
            type="button"
          >
            <I.sparkles size={14} />
            {submitting ? 'Starting…' : `Generate ${count} post${count === 1 ? '' : 's'}`}
            {!submitting && <span className="kbd" style={{ marginLeft: 'auto' }}>⏎</span>}
          </button>
        </section>
      </aside>
    </div>
  );
}

/* ============================================================
   Done view
   ============================================================ */
function DoneView({ batch, onAgain }) {
  const total = batch?.totalItems || batch?.total_items || batch?.count || '—';
  const batchId = batch?.id || batch?.batchId || null;
  return (
    <section className="cp-done">
      <div className="cp-done-bg" />
      <div className="cp-done-inner">
        <span className="cp-done-ic"><I.check size={22} /></span>
        <span className="pill pill-success" style={{ marginTop: 16 }}>
          <span className="dot" />Batch started · {total} post{total === 1 ? '' : 's'}
        </span>
        <h2 className="cp-done-h">
          Done. Your posts will be<br />
          <span className="cp-done-ac">live in the library.</span>
        </h2>
        <p className="cp-done-p">
          The activity pill at the top tracks progress. Click it any time, or open the live view below.
        </p>
        <div className="cp-done-actions">
          {batchId ? (
            <Link className="btn btn-primary btn-lg" to={`/app/batch/${batchId}`}>
              <I.sparkles size={14} /> Watch batch live
            </Link>
          ) : (
            <Link className="btn btn-primary btn-lg" to="/app/library?filter=generating">
              <I.library size={14} /> Open Library
            </Link>
          )}
          <Link className="btn btn-ghost btn-lg" to="/app/library?filter=generating">
            <I.library size={14} /> Library
          </Link>
          <Link className="btn btn-ghost btn-lg" to="/app/calendar">
            <I.calendar size={14} /> Calendar
          </Link>
        </div>
        <button className="cp-done-again" onClick={onAgain} type="button">
          <I.refresh size={12} /> Generate another batch
        </button>
      </div>
    </section>
  );
}

/* ============================================================
   Top-level CreatePost
   ============================================================ */
export const CreatePost = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState('compose'); // compose | generating | done (dev-only)
  const [lastBatch, setLastBatch] = useState(null);
  const { activeBatches } = useContentFeedContext();
  const { setActivity } = useActivity();

  // Dev-only state switcher gated by ?dev=1
  const showDevSwitch = searchParams.get('dev') === '1';

  const activeBatch = useMemo(() => {
    return (activeBatches || []).find((b) => ['queued', 'generating', 'reserved'].includes(b.status));
  }, [activeBatches]);

  // Mirrors the assistant flow: after createBatch succeeds, take the user
  // straight to /app/batch/:id where they can watch progress live. The old
  // "generating" + "done" intermediate views were a misleading double-step
  // — the batch page already handles both states and is the source of truth.
  const handleGenerated = (batch) => {
    setLastBatch(batch);
    setActivity({
      kind: 'active',
      current: batch?.readyCount || 0,
      total: batch?.totalItems || batch?.total_items || batch?.count || 0,
      step: 'Generating posts',
      project: batch?.topic || 'Content batch',
    });
    const id = batch?.id || batch?.batchId;
    if (id) {
      navigate(`/app/batch/${id}`);
    }
  };

  return (
    <div className="cp-page">
      <div className="cp-root">
        <div className="cp-top">
          <div>
            <h1 className="cp-h">Create posts</h1>
            <p className="cp-sub">
              Generate a batch tuned to your brand voice, audience, and visual. Posts go straight to your library.
            </p>
          </div>
          {showDevSwitch && (
            <div className="scen-switch">
              <span className="scen-l">State</span>
              {['compose', 'generating', 'done'].map((s) => (
                <button
                  key={s}
                  className={state === s ? 'is-active' : ''}
                  onClick={() => setState(s)}
                  type="button"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {state === 'compose' && <ComposeForm onGenerated={handleGenerated} />}

        {state === 'generating' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <ActiveJobsWidget
              job={{
                project: lastBatch?.topic || 'Generating batch',
                current: activeBatch?.ready_count || activeBatch?.readyCount || lastBatch?.readyCount || 0,
                total: activeBatch?.total_items || activeBatch?.totalItems || lastBatch?.totalItems || lastBatch?.count || 0,
                step: 'Generating posts',
              }}
              onOpenBatch={() => {
                const id = lastBatch?.id || activeBatch?.id;
                window.location.href = id ? `/app/batch/${id}` : '/app/library?filter=generating';
              }}
            />
            <p style={{ textAlign: 'center', color: 'var(--text-subtle)', fontSize: 13 }}>
              You can leave this page — the activity pill in the topbar will keep tracking.
            </p>
          </div>
        )}

        {state === 'done' && (
          <DoneView
            batch={lastBatch}
            onAgain={() => {
              setState('compose');
              setLastBatch(null);
              setActivity(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CreatePost;
